import {
  createAsyncThunk,
  createSlice,
  createSelector
} from "@reduxjs/toolkit";
import { getIntegrationsRequest } from "Api/integration/getIntegrations.request";
import { getIntegrationRequest } from "Api/integration/getIntegration.request";
import { updateIntegrationRequest } from "Api/integration/updateIntegration.request";
import { addIntegrationRequest } from "Api/integration/addIntegration.request";
import { deleteIntegrationRequest } from "Api/integration/deleteIntegration.request";
import consoleConfig from "console_config";
import { THIRD_PARTIES, ThirdParty } from "Constants/integrationThirdParties";
import logger from "Libs/logger";
import { setDeep } from "Libs/objectAccess";
import { selectIsFlexOrganization } from "Reducers/organization/organization";
import { AsyncThunkOptionType, GitErrorType } from "Reducers/types";
import { RootState } from "Store/configureStore";
import {
  APIError,
  AddIntegrationParams,
  IntegrationErrors,
  UpdateIntegrationParams,
  DeleteIntegrationParams,
  GitRepoParams,
  SortingValues,
  SelectorParams,
  Config,
  RemoteGitProject,
  RemoteGitData
} from "./types";

import type { Integration, BaseIntegration } from "Api/integration/types";

type SortDirections = "asc" | "desc";

export const getCategory = (obj?: BaseIntegration) => {
  if (!obj) return "";
  const category = THIRD_PARTIES.find(elt => elt.type === obj.type)?.category;
  if (category) return category;
  const typeSplit = obj.type.split(".");
  return typeSplit[typeSplit.length - 1];
};

const CONFIG: Record<string, Config> = {
  github: {
    tokenUrl: "/user",
    repoUrl: "/user/repos?per_page=100&page=1"
  },
  gitlab: {
    tokenUrl: "/api/v4/user",
    repoUrl: "/api/v4/projects?membership=1&per_page=100&page=1"
  }
};

const isAPIError = (error: any): error is APIError =>
  typeof error.message === "string" && error.detail;

export const getIntegrations = createAsyncThunk(
  "app/integrations",
  async ({ projectId }: { projectId: string }) => {
    const integrations = await getIntegrationsRequest(projectId);

    return [...integrations].map(integration => ({
      ...integration,
      category: getCategory(integration)
    })) as Integration[];
  }
);

export const getIntegration = createAsyncThunk(
  "app/integration",
  async (
    {
      projectId,
      integrationId
    }: {
      projectId: string;
      integrationId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const integration = await getIntegrationRequest({
        projectId,
        integrationId
      });
      return { ...integration, category: getCategory(integration) };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const addIntegration = createAsyncThunk<
  Integration,
  AddIntegrationParams,
  AsyncThunkOptionType<IntegrationErrors>
>(
  "app/integration/add",
  async ({ project, type, data }, { rejectWithValue }) => {
    try {
      const result = await addIntegrationRequest({
        projectId: project.id,
        data: { type, ...data }
      });

      const integration = result._embedded.entity;
      return { ...integration, category: getCategory(integration) };
    } catch (error: unknown) {
      let errorMessage =
        "An error occurred while attempting to add integration.";
      if (isAPIError(error)) {
        logger(error, {
          action: "addIntegration",
          meta: {
            projectId: project.id
          }
        });

        if (typeof error.detail === "object") {
          const errValues = Object.values(error.detail).join(" ");
          // The error returned by git in the type field
          errorMessage = error.detail.type ?? (errValues || error.message);
        } else {
          errorMessage = error.detail;
        }
      }
      return rejectWithValue({ errorMessage, error: error as GitErrorType });
    }
  }
);

export const updateIntegration = createAsyncThunk<
  Integration,
  UpdateIntegrationParams,
  AsyncThunkOptionType<IntegrationErrors>
>(
  "app/integration/update",
  async ({ projectId, integration, data }, { rejectWithValue }) => {
    try {
      const result = await updateIntegrationRequest({
        integration,
        data
      });

      const updatedIntegration = result._embedded.entity;
      return {
        ...updatedIntegration,
        category: getCategory(updatedIntegration)
      };
    } catch (error: unknown) {
      let errorMessage =
        "An error occurred while attempting to update integration.";
      if (isAPIError(error)) {
        logger(
          {
            errMessage: error.message,
            integrationId: integration.id,
            projectId
          },
          {
            action: "updateIntegration"
          }
        );
        if (typeof error.detail === "object") {
          // The error returned by git in the type field
          errorMessage =
            error.detail.type || error.detail?.error || error.message;
        } else {
          errorMessage = error.detail;
        }
      }
      return rejectWithValue({ errorMessage, error: error as GitErrorType });
    }
  },
  {
    condition: ({ integration }) => !!integration._links?.["#edit"]?.href
  }
);

export const deleteIntegration = createAsyncThunk<
  Integration | undefined,
  DeleteIntegrationParams,
  AsyncThunkOptionType<IntegrationErrors>
>(
  "app/integration/delete",
  async ({ projectId, integration }, { rejectWithValue }) => {
    try {
      await deleteIntegrationRequest(integration!);

      return integration;
    } catch (error) {
      const errMessage = JSON.parse(error as string);
      logger(
        {
          errMessage,
          integrationId: integration!.id,
          projectId
        },
        {
          action: "deleteIntegration"
        }
      );

      return rejectWithValue({
        errorMessage: errMessage.error,
        error: error as GitErrorType
      });
    }
  },
  {
    condition: ({ integration }) => !!integration?._links?.["#delete"]?.href
  }
);

/**
 * Transform string returns by the api like:
 * <https://gitlab.com/api/v4/users/....>; rel="first", <https://gitlab.com/api/v4/users/...>; rel="next", <https://gitlab.com/api/v4/users/...>; rel="last"
 *
 * to object
 * { first: "url", next: "url", last: "url" }
 */
const getLinks = (str: string | null) => {
  if (!str) return {};
  return str.split(", ").reduce(
    (acc, cu) => {
      const [link, val] = cu.split("; ");
      const key = val.substring(val.indexOf('"') + 1, val.lastIndexOf('"'));
      acc[key] = link.replace(/[<>]/g, "");
      return acc;
    },
    {} as Record<string, string>
  );
};

const fetchNextProjectData = async (
  url: string,
  token: string,
  projectsArray: RemoteGitProject[] = []
): Promise<RemoteGitProject[]> => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const projectData = await response.json();
  projectsArray.push(...projectData);

  const links = getLinks(response.headers.get("Link"));
  if (links.next) {
    return fetchNextProjectData(links.next, token, projectsArray);
  }
  return projectsArray;
};

export const getGitRepositoriesIntegration = createAsyncThunk<
  RemoteGitData | undefined,
  GitRepoParams,
  AsyncThunkOptionType<IntegrationErrors>
>(
  "app/integration/git",
  async ({ baseUrl, token, type }, { rejectWithValue }) => {
    if (!["gitlab", "github"].includes(type)) return;

    const apiUrl =
      baseUrl?.replace(/\/$/, "") ||
      (type === "github" ? consoleConfig.URL_GITHUB : consoleConfig.URL_GITLAB);
    const config = CONFIG[type];

    try {
      const resUser = await fetch(`${apiUrl}${config.tokenUrl}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (resUser?.status === 401) throw new Error("Bad Credentials");

      const userData: {
        avatar_url?: string;
        login?: string;
        name?: string;
        username?: string;
      } = await resUser.json();

      const projectData = await fetchNextProjectData(
        `${apiUrl}${config.repoUrl}`,
        token
      );

      return {
        repositories: projectData.map(repository => {
          const repo =
            type === "github"
              ? repository.full_name
              : repository.path_with_namespace;
          return { label: repo, value: repo };
        }),
        user: {
          name: userData.name || userData.login || userData.username,
          avatarUrl: userData.avatar_url
        }
      };
    } catch (error: unknown) {
      let errorMessage =
        "An error occurred while attempting to get Git repositories.";
      if (isAPIError(error)) {
        logger(
          { errorMessage: error.message, token: token },
          { action: "getGitRepositoriesIntegration" }
        );
        errorMessage = error.message;
      }
      return rejectWithValue({ errorMessage, error: error as GitErrorType });
    }
  }
);

export type IntegrationState = {
  data: {
    [projectId: string]:
      { [integrationId: string]: Integration | undefined } | undefined;
  };
  loadingList: boolean;
  // Per-project flag set the first time `getIntegrations.fulfilled` lands for
  // that project. Stays true across subsequent fetches so consumers can tell
  // "first load complete" from "any fetch in flight" — the global
  // `loadingList` toggles on every poll and can't carry that distinction.
  loadedProjects: { [projectId: string]: true };
  loadingSingle: boolean | undefined;
  errorMessage?: string;
  error?: GitErrorType;
  git?: {
    status?: "pending" | "fulfilled" | "rejected";
    currentToken?: string;
    data?: {
      [token: string]: RemoteGitData | undefined;
    };
  };
  lastEdited?: string;
  status?: "idle" | "added" | "rejected" | "pending" | "updated" | "deleted";
};

const initialState: IntegrationState = {
  data: {},
  loadingList: false,
  loadedProjects: {},
  loadingSingle: undefined
};

const integrations = createSlice({
  name: "integrations",
  initialState,
  reducers: {
    initForm(state, action) {
      delete state.errorMessage;
      delete state.error;
      state.status = "idle";
      state.lastEdited = action?.payload?.id;
    },
    initGit(state) {
      delete state.git?.status;
      delete state.git?.currentToken;
    },
    resetGit(state) {
      delete state.git;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getIntegrations.pending, state => {
        state.loadingList = true;
      })
      .addCase(getIntegrations.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        state.data = action.payload.reduce(
          (projectIntegrations, integration) => {
            setDeep(
              projectIntegrations,
              [projectId, integration.id],
              integration
            );
            return projectIntegrations;
          },
          {} as {
            [projectId: string]:
              { [integrationId: string]: Integration } | undefined;
          }
        );
        state.loadedProjects[projectId] = true;
        state.loadingList = false;
        state.status = "idle";
        delete state.lastEdited;
      })

      .addCase(getIntegrations.rejected, (state, action) => {
        state.errorMessage = action.error.message;
        state.error = action.error as unknown as GitErrorType;
        state.loadingList = false;
      })
      .addCase(getIntegration.pending, state => {
        state.loadingSingle = true;
        delete state.errorMessage;
        delete state.error;
      })
      .addCase(getIntegration.fulfilled, (state, action) => {
        const { projectId, integrationId } = action.meta.arg;
        setDeep(state, ["data", projectId, integrationId], action.payload);
        state.loadingSingle = false;
      })
      .addCase(getIntegration.rejected, (state, action) => {
        state.errorMessage = action.error.message;
        state.error = action.error as unknown as GitErrorType;
        state.loadingSingle = false;
      })

      .addCase(getGitRepositoriesIntegration.pending, state => {
        state.loadingList = true;
        delete state.errorMessage;
        delete state.error;
        setDeep(state, ["git", "status"], "pending");
      })
      .addCase(getGitRepositoriesIntegration.fulfilled, (state, action) => {
        const { token } = action.meta.arg;
        setDeep(state, ["git", "data", token], action.payload);
        setDeep(state, ["git", "currentToken"], token);
        state.loadingList = false;
        setDeep(state, ["git", "status"], "fulfilled");
      })

      .addCase(getGitRepositoriesIntegration.rejected, (state, action) => {
        state.errorMessage = action.payload?.errorMessage;
        state.loadingList = false;
        setDeep(state, ["git", "status"], "rejected");
      })
      .addCase(addIntegration.pending, state => {
        state.status = "pending";
        delete state.errorMessage;
        delete state.error;
      })
      .addCase(addIntegration.fulfilled, (state, action) => {
        const { project } = action.meta.arg;
        setDeep(state, ["data", project.id, action.payload.id], action.payload);
        state.status = "added";
        state.lastEdited = action.payload?.id;
      })
      .addCase(addIntegration.rejected, (state, action) => {
        state.errorMessage = action.payload?.errorMessage;
        state.error = action.payload?.error;
        state.status = "rejected";
      })
      .addCase(updateIntegration.pending, state => {
        state.status = "pending";
        delete state.errorMessage;
        delete state.error;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["data", projectId, action.payload.id], action.payload);
        state.status = "updated";
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.error = action.payload?.error;
        state.errorMessage = action.payload?.errorMessage;
        state.status = "rejected";
      })
      .addCase(deleteIntegration.pending, state => {
        state.status = "pending";
        delete state.errorMessage;
        delete state.error;
      })

      .addCase(deleteIntegration.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        const project = state.data[projectId];
        if (action.payload && project) {
          project[action.payload.id] = undefined;
        }
        delete state.errorMessage;
        delete state.error;
        state.status = "deleted";
      })
      .addCase(deleteIntegration.rejected, (state, action) => {
        state.errorMessage = action.payload?.errorMessage;
        state.error = action.payload?.error;
        state.status = "rejected";
      });
  }
});

export const { initForm, initGit, resetGit } = integrations.actions;
export default integrations.reducer;

const selectSelf = (state: RootState) => {
  return state.integration;
};

export const numberOfIntegrationsSelector = createSelector(
  selectSelf,
  (_: RootState, params: SelectorParams) => params,
  (integration, { projectId }) => {
    return Object.keys(integration.data[projectId] || {}).length;
  }
);

export const singleIntegrationSelector = createSelector(
  selectSelf,
  (_: RootState, params: SelectorParams) => params,
  (integration, { integrationId, projectId }) => {
    const singleIntegration = integration.data[projectId]?.[integrationId!];

    if (!singleIntegration) return;

    return singleIntegration;
  }
);

const getIntegrationsSorter =
  (sortBy: SortingValues, sortOrder: SortDirections) =>
  (a: Integration, b: Integration) => {
    let catA = a[sortBy] as string;
    let catB = b[sortBy] as string;

    if (!catA || !catB) return 0;

    // We need to deal with a special case when sorting by integration type
    // some types include the prefix health, but this is removed later in the
    // translation string, if we sort by the internal type the order won't make
    // sense to the user. (i.e. health.email would be after gitlab, when the name
    // we use is just email)
    if (sortBy === "type" && catA.startsWith("health.")) {
      catA = catA.split(".")[1];
    }

    if (sortBy === "type" && catB.startsWith("health.")) {
      catB = catB.split(".")[1];
    }

    if (sortOrder === "desc") {
      return catA > catB ? -1 : catA < catB ? 1 : 0;
    }

    return catA < catB ? -1 : catA > catB ? 1 : 0;
  };

export const integrationsSelector = createSelector(
  selectSelf,
  (_: RootState, params: SelectorParams) => params,
  (integration, { projectId, sortBy = "type", sortOrder = "desc" }) => {
    const sorter = getIntegrationsSorter(sortBy, sortOrder);

    return Object.values(integration.data[projectId] ?? {})
      .map(elt =>
        Object.assign({}, elt, {
          category: getCategory(elt)
        })
      )
      .sort(sorter);
  }
);

export const integrationsLoadingSelector = createSelector(
  selectSelf,
  integration => {
    return integration.loadingList;
  }
);

// True once `getIntegrations.fulfilled` has landed for this project at least
// once. Use this instead of `integrationsLoadingSelector` when gating UI that
// should survive subsequent re-fetches (e.g. the poll-driven refetch from
// `projects.listeners` while a GitHub-App integration is pending).
export const selectHasLoadedIntegrationsForProject = (
  state: RootState,
  params: { projectId: string }
): boolean => Boolean(state.integration.loadedProjects?.[params.projectId]);

export const integrationLoadingSelector = createSelector(
  selectSelf,
  integration => {
    return integration.loadingSingle;
  }
);

export const integrationsStatusSelector = createSelector(
  selectSelf,
  integration => {
    return integration.status;
  }
);

export const integrationErrorSelector = (state: RootState) => ({
  error: state.integration.error,
  errorMessage: state.integration.errorMessage
});

const selectCurrentOrganizationAvailability = (state: RootState) =>
  selectIsFlexOrganization(state) ? "flexible" : "fixed";

export const selectAvailableThirdParties = createSelector(
  selectCurrentOrganizationAvailability,
  (orgAvailability): ThirdParty[] =>
    THIRD_PARTIES.filter(
      tp => !tp.availableFor || tp.availableFor.includes(orgAvailability)
    )
);
